/**
 * Shared deep-link routing for notifications.
 *
 * Used by:
 *  - `components/notifications/NotificationRow.tsx` — bell-feed taps on
 *    a `NotificationRow` from `GET /notifications`.
 *  - `native/notifications.ts` — mobile FCM push taps, where the
 *    notification's FCM `data` map carries the same producer-stamped
 *    fields (after MV2-341 forwarded `payload` scalars over FCM).
 *
 * One canonical implementation so both surfaces agree on what
 * `ticket.comment_on_watched` (and friends) deep-link to — pre-MV2-340,
 * the mobile router treated `data.related_id` as the ticket id but the
 * producer stamps the *comment* id there (for the bell-feed scroll
 * anchor); the parent ticket id lives at `payload.ticket_id`.
 */

/** Minimum structural shape needed to compute a deep-link target.
 *
 * Both `NotificationRow` (webapp) and the FCM push data map (mobile)
 * can be coerced into this without a wrapper type — for push, the
 * FCM `data` map itself is the "payload" since the dispatcher merges
 * scalars in (MV2-341).
 */
export interface NotificationRouteInput {
  category: string;
  event: string;
  related_type: string | null;
  related_id: string | null;
  payload: unknown;
}

/** Read a string key out of a JSON-shaped payload; null if absent or
 * non-string. Defensive against null and non-object inputs so callers
 * can pass row payloads directly without pre-narrowing. */
export function payloadString(payload: unknown, key: string): string | null {
  if (payload === null || typeof payload !== "object") return null;
  const v = (payload as Record<string, unknown>)[key];
  return typeof v === "string" ? v : null;
}

/**
 * Resolve a notification's click / tap target. Spec §3.2.
 *
 * For mentions and watched-comment events, ``related_id`` is the
 * *comment* id (not the ticket). The dispatcher stamps the actual
 * ticket id into ``payload.ticket_id`` — pull from there first,
 * fall back to ``related_id`` only when payload is missing
 * (defensive — pre-MV2-217 hotfix rows have no payload field).
 */
export function rowHref(row: NotificationRouteInput): string {
  const cat = row.category;
  const rel = row.related_id;
  const ticketId = payloadString(row.payload, "ticket_id");
  const commentId =
    payloadString(row.payload, "comment_id") ??
    (row.related_type === "ticket_comment" ? rel : null);

  if (cat === "mention" && (ticketId || rel)) {
    // payload.ticket_id is canonical; rel is the comment id for the
    // comment case or the ticket id for the body case.
    const target = ticketId ?? (row.related_type === "ticket_body" ? rel : null);
    if (target) {
      return commentId ? `/tickets/${target}?comment_id=${commentId}` : `/tickets/${target}`;
    }
  }

  if (cat === "ticket") {
    // Watched-comment + mention-shaped events on the ticket category
    // (rare — most mentions land in cat=mention) need the same
    // ticket-id-from-payload routing.
    if (row.event === "ticket.comment_on_watched" || row.event.startsWith("mention.")) {
      const target = ticketId ?? rel;
      if (target) {
        return commentId ? `/tickets/${target}?comment_id=${commentId}` : `/tickets/${target}`;
      }
    }
    if (rel) return `/tickets/${rel}`;
    if (ticketId) return `/tickets/${ticketId}`;
  }

  if (cat === "alarm" && rel) return `/alarms/${rel}`;
  if (cat === "report") return "/reports";

  if (cat === "export") {
    // CSV export-ready (MV2-470). Land on the device's Historical tab with the
    // just-finished job highlighted in the Recent exports panel (ChartAnyField
    // reads ?exportsHighlight). The dispatcher stamps site_id/device_id/job_id
    // into payload; build the URL here — not from payload.deep_link — so the
    // route shape stays owned by the SPA router (matches spec §3.2). The
    // devices route is flat (/sites/:siteId/devices); device + tab are query
    // params, not path segments.
    const siteId = payloadString(row.payload, "site_id");
    const deviceId = payloadString(row.payload, "device_id");
    const jobId = payloadString(row.payload, "job_id") ?? rel; // related_id IS the job id
    if (siteId && deviceId) {
      const highlight = jobId ? `&exportsHighlight=${jobId}` : "";
      return `/sites/${siteId}/devices?device=${deviceId}&tab=historical${highlight}`;
    }
    // site_timeseries export (no device) — not UI-triggered yet; land on the site.
    if (siteId) return `/sites/${siteId}`;
  }

  if (cat === "account" && row.event.startsWith("account.security_")) {
    return "/settings/security";
  }
  if (cat === "system") return "/notifications"; // inline expand in v2
  return "/notifications";
}
